import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useAuth } from "shared/lib/useAuth"
import { useBooleanState } from "shared/lib/useBooleanState"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"

import { useProjectData } from "entities/projects/api/useProjectData"
import { useDeleteTeamMember } from "entities/team-members/api/useDeleteTeamMember"
import { useUpdateTeamMember } from "entities/team-members/api/useUpdateTeamMember"
import { useTeamMemberByEmail } from "entities/team-members/model/useTeamMemberByEmail"
import { TeamRole } from "entities/team-members/types/TeamRole"
import { useTeamData } from "entities/teams/model/useTeam"
import z from "zod"

const teamRoleSchema = z.enum(TeamRole)

export interface AdminTeamLeaveFormData {
  memberID: string
}

interface UseTeamLeaveOverlayModelProps {
  projectID: string
}

export interface TeamLeaveOverlayModel {
  role: TeamRole | null | undefined
  wasLastAdmin: boolean
  isLastTeamMember: boolean
  isLastAdmin: boolean
  isUpdatingAdmin: boolean
  hasPendingInvites: boolean
  teamMemberOptions: { label: string; value: string; data: unknown }[]
  onUpdateAdmin: (data: AdminTeamLeaveFormData) => void
  onLeaveTeam: () => void
  onCancel: () => void
}

export function useTeamLeaveOverlayModel({
  projectID
}: UseTeamLeaveOverlayModelProps): TeamLeaveOverlayModel {
  const { t } = useTranslation()
  const {
    value: isUpdatingAdmin,
    setTrue: setIsSubmitting,
    setFalse: setIsSubmittingFalse
  } = useBooleanState()
  const { role, project } = useProjectData(projectID)
  const { team, isLoading } = useTeamData({ teamID: project?.team?.id || "" })
  const { userInfo } = useAuth()
  const navigate = useNavigate()
  const { closeOverlay } = useCurrentOverlay()
  const { updateTeamMember } = useUpdateTeamMember()
  const { deleteTeamMember } = useDeleteTeamMember()

  const messages = useMemo(
    () => ({
      success: t("Request.TeamMember.LeaveTeam.Success", {
        projectName: project?.title
      }),
      failure: t("Request.TeamMember.LeaveTeam.Failure"),
      loading: t("Request.TeamMember.LeaveTeam.Start")
    }),
    [t, project?.title]
  )
  const { raiseLoadingToast } = useRequestToasts({ messages })

  const myMembership = useTeamMemberByEmail({
    email: userInfo?.email || "",
    teamMembers: team?.memberships || []
  })

  const isLastAdmin = useMemo(
    () =>
      role === TeamRole.ADMIN &&
      team?.memberships.filter(
        member => teamRoleSchema.safeParse(member.role).data === TeamRole.ADMIN
      ).length === 1,
    [role, team]
  )

  const [wasLastAdmin, setWasLastAdmin] = useState(false)

  const prevIsLastAdmin = useRef(false)

  // Latch only on the true -> false transition: the user was the last admin and
  // has since handed the role over. `!isLastAdmin` alone would also fire for
  // admins who never were the last one. The isLoading gate keeps a refetch that
  // empties `team` from being mistaken for a handover.
  useEffect(() => {
    if (isLoading) return
    if (prevIsLastAdmin.current && !isLastAdmin) {
      setWasLastAdmin(true)
    }
    prevIsLastAdmin.current = isLastAdmin
  }, [isLastAdmin, isLoading])

  const otherMembers = useMemo(
    () =>
      team?.memberships.filter(
        membership => membership.user?.id !== myMembership?.user?.id
      ) ?? [],
    [team, myMembership]
  )

  const isLastTeamMember = useMemo(
    () => team !== undefined && otherMembers.length === 0,
    [team, otherMembers]
  )

  const hasPendingInvites = useMemo(
    () => otherMembers.some(membership => !membership.invitationAccepted),
    [otherMembers]
  )

  const teamMemberOptions = useMemo(
    () =>
      otherMembers
        .filter(membership => membership.invitationAccepted)
        .map(membership => ({
          label: membership.user?.name || membership.user?.email || "",
          value: membership.id,
          data: membership
        })),
    [otherMembers]
  )

  const handleUpdateAdmin = useEvent(({ memberID }: AdminTeamLeaveFormData) => {
    setIsSubmitting()
    updateTeamMember({
      teamID: team?.id || "",
      teamMemberID: memberID,
      updates: { id: memberID, role: TeamRole.ADMIN }
    }).then(() => {
      setIsSubmittingFalse()
    })
  })

  const handleLeaveTeam = useEvent(() => {
    if (!myMembership) return
    setIsSubmitting()
    const loadingToast = raiseLoadingToast()
    deleteTeamMember(
      {
        teamID: team?.id || "",
        teamMemberID: myMembership.id
      },
      { suppressToasts: true }
    ).then(({ errors }) => {
      setIsSubmittingFalse()
      if (!errors || errors.length === 0) {
        loadingToast.raiseSuccessToast()
        navigate("/projects", { replace: true })
        closeOverlay()
      } else {
        loadingToast.raiseErrorToast()
      }
    })
  })

  return {
    role,
    wasLastAdmin,
    isLastTeamMember,
    isLastAdmin,
    isUpdatingAdmin,
    hasPendingInvites,
    teamMemberOptions,
    onUpdateAdmin: handleUpdateAdmin,
    onLeaveTeam: handleLeaveTeam,
    onCancel: closeOverlay
  }
}
